import { DateTime } from 'luxon';
import { BOOKING_CUTOFF_HOUR } from '../constants';
import { RatingType } from '../enums/rating-types';
import { ICalendarDay } from '../interfaces/c-date';
import { IListing } from '../interfaces/listing';
import { IRating } from '../interfaces/rating';
import { ISite } from '../interfaces/site';

export function containsCompleteRating(ratings?: IRating[]): boolean {
  return !!ratings?.find((rating) => rating.topic === RatingType.overall && rating.score.description);
}

export function isDateValidForSite(date: string, site: ISite, listing: IListing): boolean {
  if (!site || !listing) {
    return false;
  }

  // Filter out Blackout Days - Listing
  if (listing.blackoutDays && listing.blackoutDays.length > 0) {
    for (const blackoutDateRange of listing.blackoutDays) {
      if (blackoutDateRange.includes(':')) {
        const blackoutSplit = blackoutDateRange.split(':');
        const blackoutDateStart = DateTime.fromFormat(blackoutSplit[0], 'yyyy-MM-dd');
        const blackoutDateEnd = DateTime.fromFormat(blackoutSplit[1], 'yyyy-MM-dd');
        if (DateTime.fromISO(date) >= blackoutDateStart && DateTime.fromISO(date) <= blackoutDateEnd) {
          return false;
        }
      } else {
        const blackoutDate = DateTime.fromFormat(blackoutDateRange, 'yyyy-MM-dd');
        if (DateTime.fromISO(date) === blackoutDate) {
          return false;
        }
      }
    }
  }

  // Filter out Blackout Months - Listing
  if (listing.blackoutMonths && listing.blackoutMonths.length > 0) {
    for (const blackoutMonth of listing.blackoutMonths) {
      if (DateTime.fromISO(date).month === blackoutMonth) {
        return false;
      }
    }
  }

  // Filter out Blackout Months - Day of the Week
  if (listing.blackoutDayOfWeek && listing.blackoutDayOfWeek.length > 0) {
    for (const blackoutDayOfWeek of listing.blackoutDayOfWeek) {
      if (DateTime.fromISO(date).weekdayLong?.toLowerCase() === blackoutDayOfWeek.toLowerCase()) {
        return false;
      }
    }
  }

  // Filter out Blackout Days - Site
  if (site.blackoutDays && site.blackoutDays.length > 0) {
    for (const blackoutDateRange of site.blackoutDays) {
      if (blackoutDateRange.includes(':')) {
        const blackoutSplit = blackoutDateRange.split(':');
        const blackoutDateStart = DateTime.fromFormat(blackoutSplit[0], 'yyyy-MM-dd');
        const blackoutDateEnd = DateTime.fromFormat(blackoutSplit[1], 'yyyy-MM-dd');
        if (DateTime.fromISO(date) >= blackoutDateStart && DateTime.fromISO(date) <= blackoutDateEnd) {
          return false;
        }
      } else {
        const blackoutDate = DateTime.fromFormat(blackoutDateRange, 'yyyy-MM-dd');
        if (DateTime.fromISO(date) === blackoutDate) {
          return false;
        }
      }
    }
  }

  // Filter out Blackout Months - Site
  if (site.blackoutMonths && site.blackoutMonths.length > 0) {
    for (const blackoutMonth of site.blackoutMonths) {
      if (DateTime.fromISO(date).month === blackoutMonth) {
        return false;
      }
    }
  }

  return DateTime.now() < bookingCutoffFor(date, listing);
}

/**
 * Notice a listing requires, in days (reserveDaysInAdvance), 0 when unset. Can be fractional:
 * the host booking form offers 0.5 for 12 hours.
 */
export function minNoticeDays(listing: Pick<IListing, 'reserveDaysInAdvance'>): number {
  const days = Number(listing.reserveDaysInAdvance || 0);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

/**
 * The moment a check-in on `date` stops being bookable: BOOKING_CUTOFF_HOUR (10pm) on the
 * check-in day, less the listing's notice, in the listing's timezone. Whole days step back by
 * calendar day (so 1 day is 10pm the night before, across DST too) and any fraction by minutes,
 * since luxon only takes whole calendar units (0.5 days is 10am on the check-in day).
 */
export function bookingCutoffFor(date: string, listing: Pick<IListing, 'reserveDaysInAdvance' | 'timezone'>): DateTime {
  const notice = minNoticeDays(listing);
  const wholeDays = Math.floor(notice);
  const extraMinutes = Math.round((notice - wholeDays) * 24 * 60);
  return DateTime.fromISO(date, { zone: listing.timezone || undefined })
    .set({ hour: BOOKING_CUTOFF_HOUR, minute: 0, second: 0, millisecond: 0 })
    .minus({ days: wholeDays })
    .minus({ minutes: extraMinutes });
}

/** The first check-in date (ISO, listing timezone) that's still before its booking cutoff. */
export function earliestBookableDate(
  listing: Pick<IListing, 'reserveDaysInAdvance' | 'timezone'>,
  now: DateTime = DateTime.now(),
): string {
  // runs at most ceil(notice) + 1 times: each step moves the cutoff a day later
  let day = now.setZone(listing.timezone || 'local').startOf('day');
  while (now >= bookingCutoffFor(day.toISODate() as string, listing)) {
    day = day.plus({ days: 1 });
  }
  return day.toISODate() as string;
}

export function filterAvailableDays(availableDays: ICalendarDay[], site: ISite, listing: IListing): ICalendarDay[] {
  return availableDays.filter((day) => isDateValidForSite(day.date, site, listing));
}
